// @refresh reset
// ─────────────────────────────────────────────────────────────────────────────
// THE PRICING SHEET — an interactive calculator, not a document.
//
// Erez, 2026-07-27: "I want to see it as a sheet but I don't want to go out to
// a sheet. Build it so I can see all the numbers and actually change them and
// play with it."
//
// So: every blue number is an input. Change a knob and every total recomputes
// live. Nothing here is stored — it is a what-if surface. The DEFAULTS are the
// calibrated set that lands Phase 1 at about $40,000, which is the target Erez
// set after judging the first pass too expensive.
//
// Two rules this thing encodes, both learned the hard way:
//   1. The user multiplier is BANDED and capped at 1.75x. A literal headcount
//      multiplier prices a $7,500 feature at $75,000 and is not real.
//   2. Users means WHO OPENS THE SCREEN, never how many records it processes.
//      The commission run touches ~30 commissions and has 2-3 users.
//
// 🔴 in a cell = a figure we estimated, not one the client stated. Only two
// headcount numbers in this whole model are real (~30 commissions, ~25 payroll).
//
// Owner agent: Hank.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useMemo } from "react";
import { N, NFONT, NMONO, R, R_CTL } from "./notion";

const F = NFONT;
const MONO = NMONO;

// Notion palette, mapped onto the names this file already used so the styling
// swap stays a one-line change per rule rather than a rewrite.
const C = {
  ink: N.ink, ink2: N.ink2, ink3: N.ink3,
  line: N.line, lineHard: N.lineStrong,
  bg: N.bg, head: N.bgGray, zebra: N.bg,
  blue: N.blue, blueSoft: N.blueBg,
  green: N.green, greenSoft: N.greenBg,
  amber: N.yellow, amberSoft: N.yellowBg,
  red: N.red, redSoft: N.redBg,
};

type Size = "S" | "M" | "L" | "?";

type Feature = {
  id: string;
  name: string;
  note: string;
  size: Size;
  /** Who OPENS the screen. null = we genuinely do not know. */
  users: number | null;
  /** true when the figure is our estimate rather than something they said. */
  est: boolean;
  phase1: boolean;
};

/** The nine quotable features plus the one we honestly cannot size.
 *  Every row traces to a problem raised on 23 July 2026. */
const FEATURES: Feature[] = [
  { id: "f1", name: "Definitions catalogue", note: "a job, a lead, a source defined once and applied everywhere", size: "M", users: 4, est: true, phase1: true },
  { id: "f2", name: "Automated reporting", note: "a month's spend never measured against another month's leads, sliceable by market", size: "M", users: 6, est: true, phase1: true },
  { id: "f3", name: "Ask in plain language", note: "the brain, answers with lineage — what Lane built and took", size: "L", users: 3, est: true, phase1: true },
  { id: "f4", name: "Schema-drift watch + repair", note: "Workiz renames a column, it heals and says so", size: "S", users: 0, est: false, phase1: true },
  { id: "f5", name: "Speed-to-lead, in-house", note: "retires Callingly", size: "L", users: 0, est: false, phase1: false },
  { id: "f6", name: "Capacity model + schedule solver", note: "real availability, no overbooking, after-hours honest", size: "L", users: 5, est: true, phase1: false },
  { id: "f7", name: "Running-late handoff", note: "agent drafts the message, a person sends it", size: "M", users: 0, est: false, phase1: false },
  { id: "f8", name: "The commission run", note: "computes the cycle, flags the last 10%, one approval", size: "L", users: 3, est: true, phase1: false },
  { id: "f9", name: "Call visibility", note: "handle time, abandoned rate — depends on what CallRail exposes", size: "?", users: null, est: true, phase1: false },
  // f10 RESTORED 2026-07-27: Erez put "Permissions and a safety gate" back into
  // the email he is actually sending, so the sheet has to be able to price what
  // we are asking them to confirm. (It had been cut on the reasoning that
  // guardrails are part of building the system, not a sellable feature — his
  // copy overrides that.) Whole system goes back to $77,500; Phase 1 unaffected.
  { id: "f10", name: "Permissions and a safety gate", note: "what runs on its own vs what needs approval, plus access per person", size: "M", users: 2, est: true, phase1: false },
];

/** Calibrated 2026-07-27 to land Phase 1 near $40,000 (Erez's target).
 *  The first pass ran $2,500 / $5,000 / $9,500 and produced $48,575. */
const DEFAULT_KNOBS = {
  S: 1200, M: 2000, L: 4500,
  b1: 1.0, b2: 1.1, b3: 1.2, b4: 1.3,
  discovery: 2500, os: 1500, warehouse: 1000,
  connectorPrice: 0, connectorCount: 6,
  retainer: 2500,
};

type Knobs = typeof DEFAULT_KNOBS;

const usd = (n: number) => "$" + Math.round(n).toLocaleString();

/** Banded, capped. See the header note — a linear multiplier is not real. */
function bandOf(users: number | null, k: Knobs): { m: number; label: string } {
  if (users == null) return { m: 0, label: "—" };
  // 0 is not "unknown" — it means a MECHANISM with no screen at all. Erez,
  // 2026-07-28: most of this list has no users. Self-healing connections,
  // speed-to-lead and the running-late handoff are background programs; nobody
  // opens them. Costing them as if headcount mattered was the error.
  if (users === 0) return { m: k.b1, label: "no screen" };
  if (users <= 2) return { m: k.b1, label: "1-2" };
  if (users <= 10) return { m: k.b2, label: "3-10" };
  if (users <= 30) return { m: k.b3, label: "11-30" };
  return { m: k.b4, label: "31+" };
}

function basePrice(size: Size, k: Knobs): number | null {
  if (size === "S") return k.S;
  if (size === "M") return k.M;
  if (size === "L") return k.L;
  return null;
}

/** An editable number cell. Blue = you can change it. */
function Num({
  value, onChange, width = 78, step = 100, prefix, suffix, decimals = 0,
}: {
  value: number; onChange: (n: number) => void; width?: number;
  step?: number; prefix?: string; suffix?: string; decimals?: number;
}) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      {prefix && <span style={{ color: C.blue, fontFamily: MONO, fontSize: 12.5 }}>{prefix}</span>}
      <input
        type="number"
        value={value}
        step={step}
        onChange={(e) => {
          const n = parseFloat(e.target.value);
          onChange(Number.isFinite(n) ? n : 0);
        }}
        style={{
          width, fontFamily: MONO, fontSize: 12.5, fontWeight: 500, color: N.blue,
          background: N.blueBg, border: `1px solid transparent`, borderRadius: R_CTL,
          padding: "3px 6px", textAlign: "right", outline: "none",
        }}
        onFocus={(e) => e.currentTarget.select()}
      />
      {suffix && <span style={{ color: C.ink3, fontFamily: MONO, fontSize: 11.5 }}>{suffix}</span>}
      {decimals ? null : null}
    </span>
  );
}

function Th({ children, align = "left", w }: { children: React.ReactNode; align?: "left" | "right" | "center"; w?: number }) {
  return (
    <th style={{
      textAlign: align, width: w, padding: "9px 12px", fontFamily: MONO, fontSize: 10.5,
      fontWeight: 700, letterSpacing: ".09em", textTransform: "uppercase", color: C.ink2,
      background: N.bgGray, borderBottom: `1px solid ${N.line}`, whiteSpace: "nowrap",
    }}>{children}</th>
  );
}

function Td({ children, align = "left", mono, bold, color }: {
  children: React.ReactNode; align?: "left" | "right" | "center";
  mono?: boolean; bold?: boolean; color?: string;
}) {
  return (
    <td style={{
      textAlign: align, padding: "8px 12px", fontSize: mono ? 13 : 13.5,
      fontFamily: mono ? MONO : F, fontWeight: bold ? 700 : 500,
      color: color ?? C.ink, borderBottom: `1px solid ${C.line}`, verticalAlign: "middle",
    }}>{children}</td>
  );
}

function KnobRow({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "5px 0" }}>
      <span style={{ fontSize: 12.5, color: C.ink2 }}>
        {label}
        {hint && <span style={{ color: C.ink3, fontSize: 11 }}> {hint}</span>}
      </span>
      {children}
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: N.bg, border: `1px solid ${N.line}`, borderRadius: R, padding: "12px 14px" }}>
      <div style={{
        fontFamily: MONO, fontSize: 10.5, fontWeight: 700, letterSpacing: ".09em",
        textTransform: "uppercase", color: C.ink3, marginBottom: 6,
      }}>{title}</div>
      {children}
    </div>
  );
}

function Total({ label, value, sub, tone }: { label: string; value: string; sub: string; tone: "green" | "blue" | "ink" }) {
  const map = {
    green: { bg: N.greenBg, ink: N.green },
    blue: { bg: N.blueBg, ink: N.blue },
    ink: { bg: N.grayBg, ink: N.ink },
  }[tone];
  return (
    <div style={{
      flex: 1, background: map.bg, borderRadius: R,
      padding: "13px 16px", minWidth: 190,
    }}>
      <div style={{
        fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: ".1em",
        textTransform: "uppercase", color: map.ink, opacity: .75,
      }}>{label}</div>
      <div style={{ fontFamily: F, fontSize: 30, fontWeight: 600, color: map.ink, letterSpacing: "-.02em", lineHeight: 1.15, marginTop: 2 }}>{value}</div>
      <div style={{ fontSize: 11.5, color: C.ink2, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

export function AirDuctPricingSheet() {
  const [k, setK] = useState<Knobs>(DEFAULT_KNOBS);
  const [rows, setRows] = useState<Feature[]>(FEATURES);

  const set = (key: keyof Knobs) => (n: number) => setK((p) => ({ ...p, [key]: n }));
  const patch = (id: string, d: Partial<Feature>) =>
    setRows((p) => p.map((r) => (r.id === id ? { ...r, ...d } : r)));

  const calc = useMemo(() => {
    const setup = k.discovery + k.os + k.warehouse + k.connectorPrice * k.connectorCount;
    const priced = rows.map((r) => {
      const base = basePrice(r.size, k);
      const band = bandOf(r.users, k);
      const price = base == null || band.m === 0 ? null : base * band.m;
      return { ...r, base, band, price };
    });
    const featuresAll = priced.reduce((s, r) => s + (r.price ?? 0), 0);
    const phase1Features = priced.reduce((s, r) => s + (r.phase1 ? r.price ?? 0 : 0), 0);
    const quotable = priced.filter((r) => r.price != null).length;
    const inPhase1 = priced.filter((r) => r.phase1 && r.price != null).length;
    return {
      setup, priced, featuresAll, phase1Features, quotable, inPhase1,
      phase1: setup + phase1Features,
      whole: setup + featuresAll,
    };
  }, [k, rows]);

  const sizeBtn = (r: Feature, s: Size) => {
    const on = r.size === s;
    return (
      <button
        key={s}
        onClick={() => patch(r.id, { size: s })}
        style={{
          width: 26, height: 24, borderRadius: R_CTL, cursor: "pointer",
          fontFamily: MONO, fontSize: 11.5, fontWeight: 600,
          border: `1px solid ${on ? "transparent" : N.lineSoft}`,
          background: on ? (s === "?" ? N.red : N.ink) : N.bg,
          color: on ? "#FFF" : N.ink3,
        }}
      >{s}</button>
    );
  };

  return (
    // nowheel/nodrag/nopan + stopPropagation is MANDATORY on any interactive
    // board node. Without it React Flow's panOnDrag swallows the pointer before
    // it reaches an input or a button: the thing renders perfectly, the state
    // logic is perfectly correct, and NOTHING can be clicked. Shipped that way
    // once (2026-07-27) — copied from AirDuctApp, which had it all along.
    <div
      className="nowheel nodrag nopan"
      data-zoom
      onPointerDown={(e) => e.stopPropagation()}
      style={{
        pointerEvents: "auto",
        width: 1560, background: N.bg, border: `1px solid ${N.line}`,
        borderRadius: R, padding: 26, fontFamily: F, boxSizing: "border-box",
      }}
    >
      {/* ── header + live totals ── */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 700, color: N.ink, letterSpacing: "-.02em" }}>
            The pricing sheet
          </div>
          <div style={{ fontSize: 12.5, color: C.ink2, marginTop: 3 }}>
            Every <span style={{ color: C.blue, fontWeight: 700 }}>blue</span> number is editable. Totals recompute live. Nothing is saved.
          </div>
        </div>
        <button
          onClick={() => { setK(DEFAULT_KNOBS); setRows(FEATURES); }}
          style={{
            fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: ".06em",
            textTransform: "none", color: N.ink2, background: N.bg,
            border: `1px solid ${N.lineStrong}`, borderRadius: R_CTL, padding: "6px 12px", cursor: "pointer",
          }}
        >Reset to calibrated</button>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <Total tone="green" label="Phase 1 — what we sell now" value={usd(calc.phase1)}
          sub={`setup + ${calc.inPhase1} features · target ~$40,000`} />
        <Total tone="ink" label="Setup — installing the OS" value={usd(calc.setup)}
          sub={`discovery + infra + warehouse + ${k.connectorCount} connectors`} />
        <Total tone="blue" label="The whole system" value={usd(calc.whole)}
          sub={`setup + all ${calc.quotable} quotable features`} />
        <Total tone="ink" label="Retainer" value={usd(k.retainer) + "/mo"}
          sub="runs it, keeps the brain current" />
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start" }}>
        {/* ── the knobs ── */}
        <div style={{ width: 300, flexShrink: 0, display: "flex", flexDirection: "column", gap: 10 }}>
          <Panel title="Feature base price">
            <KnobRow label="Small"><Num value={k.S} onChange={set("S")} step={250} prefix="$" /></KnobRow>
            <KnobRow label="Medium"><Num value={k.M} onChange={set("M")} step={250} prefix="$" /></KnobRow>
            <KnobRow label="Large"><Num value={k.L} onChange={set("L")} step={250} prefix="$" /></KnobRow>
          </Panel>

          <Panel title="User bands — capped multiplier">
            <KnobRow label="1-2 users"><Num value={k.b1} onChange={set("b1")} step={0.05} width={62} suffix="x" /></KnobRow>
            <KnobRow label="3-10 users"><Num value={k.b2} onChange={set("b2")} step={0.05} width={62} suffix="x" /></KnobRow>
            <KnobRow label="11-30 users"><Num value={k.b3} onChange={set("b3")} step={0.05} width={62} suffix="x" /></KnobRow>
            <KnobRow label="31+ users"><Num value={k.b4} onChange={set("b4")} step={0.05} width={62} suffix="x" /></KnobRow>
            <div style={{ fontSize: 11, color: C.ink3, lineHeight: 1.45, marginTop: 5, borderTop: `1px solid ${C.line}`, paddingTop: 6 }}>
              Ten users do not cost five times two. Permissions and training grow in steps, so the multiplier is banded and capped.
            </div>
          </Panel>

          <Panel title="Setup — the OS install">
            <KnobRow label="Discovery & analysis"><Num value={k.discovery} onChange={set("discovery")} step={250} prefix="$" /></KnobRow>
            <KnobRow label="OS on their infra"><Num value={k.os} onChange={set("os")} step={250} prefix="$" /></KnobRow>
            <KnobRow label="Warehouse + model"><Num value={k.warehouse} onChange={set("warehouse")} step={250} prefix="$" /></KnobRow>
            <KnobRow label="Per connector"><Num value={k.connectorPrice} onChange={set("connectorPrice")} step={100} prefix="$" /></KnobRow>
            <KnobRow label="Connectors" hint="🔴 6 assumed"><Num value={k.connectorCount} onChange={set("connectorCount")} step={1} width={62} suffix="x" /></KnobRow>
            <div style={{
              display: "flex", justifyContent: "space-between", marginTop: 7,
              borderTop: `1.5px solid ${C.lineHard}`, paddingTop: 7,
              fontFamily: MONO, fontSize: 13, fontWeight: 700, color: C.ink,
            }}>
              <span>SETUP</span><span>{usd(calc.setup)}</span>
            </div>
          </Panel>

          <Panel title="Retainer">
            <KnobRow label="Per month"><Num value={k.retainer} onChange={set("retainer")} step={250} prefix="$" /></KnobRow>
            <div style={{ fontSize: 11, color: C.ink3, lineHeight: 1.45, marginTop: 4 }}>
              Twelve months is {usd(k.retainer * 12)}. Headcount belongs HERE, not in the build.
            </div>
          </Panel>
        </div>

        {/* ── the feature table ── */}
        <div style={{ flex: 1, background: N.bg, border: `1px solid ${N.line}`, borderRadius: R, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <Th w={34} align="center">P1</Th>
                <Th>Feature</Th>
                <Th w={104} align="center">Size</Th>
                <Th w={86} align="right">Base</Th>
                <Th w={92} align="center">Users</Th>
                <Th w={78} align="center">Band</Th>
                <Th w={98} align="right">Price</Th>
              </tr>
            </thead>
            <tbody>
              {calc.priced.map((r, i) => {
                const dead = r.price == null;
                return (
                  <tr key={r.id} style={{ background: dead ? C.redSoft : i % 2 ? C.zebra : C.bg }}>
                    <Td align="center">
                      <input
                        type="checkbox"
                        checked={r.phase1}
                        disabled={dead}
                        onChange={(e) => patch(r.id, { phase1: e.target.checked })}
                        style={{ width: 15, height: 15, accentColor: C.green, cursor: dead ? "not-allowed" : "pointer" }}
                      />
                    </Td>
                    <Td>
                      <div style={{ fontWeight: 650, color: dead ? C.red : C.ink }}>{r.name}</div>
                      <div style={{ fontSize: 11.5, color: C.ink3, marginTop: 1, lineHeight: 1.35 }}>{r.note}</div>
                    </Td>
                    <Td align="center">
                      <div style={{ display: "inline-flex", gap: 3 }}>
                        {(["S", "M", "L", "?"] as Size[]).map((s) => sizeBtn(r, s))}
                      </div>
                    </Td>
                    <Td align="right" mono color={dead ? C.red : C.ink2}>
                      {r.base == null ? "—" : usd(r.base)}
                    </Td>
                    <Td align="center">
                      {r.users == null ? (
                        <span style={{ fontFamily: MONO, fontSize: 12, color: C.red }}>🔴 ?</span>
                      ) : (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                          {r.est && <span style={{ fontSize: 10 }} title="our estimate, not stated by the client">🔴</span>}
                          <Num value={r.users} onChange={(n) => patch(r.id, { users: Math.max(0, Math.round(n)) })} step={1} width={46} />
                        </span>
                      )}
                    </Td>
                    <Td align="center" mono color={dead ? C.red : C.ink2}>
                      {r.band.m === 0 ? "—" : `${r.band.m}x`}
                    </Td>
                    <Td align="right" mono bold color={dead ? C.red : C.ink}>
                      {r.price == null ? "not quoted" : usd(r.price)}
                    </Td>
                  </tr>
                );
              })}
              <tr style={{ background: C.head }}>
                <Td align="center"><span style={{ fontFamily: MONO, fontSize: 11, color: C.green, fontWeight: 700 }}>{calc.inPhase1}</span></Td>
                <Td bold>Totals</Td>
                <Td align="center" mono color={C.ink2}>{calc.quotable} quotable</Td>
                <Td align="right" mono color={C.ink3}>—</Td>
                <Td align="center" mono color={C.ink3}>—</Td>
                <Td align="right" mono color={C.ink3}>—</Td>
                <Td align="right" mono bold>{usd(calc.featuresAll)}</Td>
              </tr>
            </tbody>
          </table>

          <div style={{
            display: "flex", gap: 22, padding: "10px 14px", borderTop: `1px solid ${C.line}`,
            fontSize: 11.5, color: C.ink2, flexWrap: "wrap", alignItems: "center",
          }}>
            <span><b>P1</b> = in the phase we sell now</span>
            <span style={{ color: C.red }}>🔴 = our estimate, not their words</span>
            <span><b>?</b> = cannot be sized until we look — never quoted fixed</span>
            <span style={{ color: C.ink3 }}>Users = who OPENS it, never how many records it touches</span>
          </div>
        </div>
      </div>
    </div>
  );
}
